import React, {Component} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import TopSection from './sections/top_section';
import MainSection from './sections/main-section/main_section';
import OptionsSection from './sections/options_section';
import DescriptionSection from './sections/description_section';
import LabelSection from './sections/label_section';
import SubtaskSection from './sections/subtask-section/subtask_section';
import FilesSection from './sections/files_section';
import CommentSection from './sections/comment-section/comment_section';
import CommentFooterSection from './sections/comment_footer_section.js';
import {NovaConversationThread} from './sections/nova-thread/NovaConversationThread';
import {deriveNovaLandingKind} from './sections/nova-thread/novaLanding';
import FooterSection from './sections/footer-section/footer_section';
import {withRouter} from 'react-router-dom';
import Util, {MAX_FILES_PER_UPLOAD} from '../../util/util';
import DirectApi from '../../../../directApi';
import {createPendingMediaDeletes} from './inlineImages/PendingMediaDeletes';
import {removedReferences} from './inlineImages/references';
import {trackInlineMediaRemoved} from '../../../../tracking/amplitude/editorEvents';
import {createInlineMediaAdapter} from './inlineImages/useInlineMediaAdapter';
import {DropTarget} from 'react-dnd';
import {NativeTypes} from 'react-dnd-html5-backend';
import {withSocketHandling} from '../../../../socket/withSocketHandling';
import NotFoundModal from '../../../../containers/modal/not_found_modal';
import TaskModalLoader from './task_modal_v3_loader';
import {injectIntl} from 'react-intl';
import {findDOMNode} from 'react-dom';
import {PROGRAM_BUDGET_TYPE, SOCKET_ACTION, SOCKET_EVENT_TYPE} from '../../../../constants';
import UpdateTaskMutation from '../../../../mutations/update_task_mutation.modern';
import {isSimulationMode} from '../../../../components/canvas-scheduling/canvas-timeline/canvas_timeline_util';
import GithubSectionRenderer from './sections/github-section/github_section_renderer';
import * as tracking from '../../../../tracking';
import DependencySectionQueryContent, {
	DependencySectionQueryContentQuery,
} from './sections/dependency-section/DependencySectionQueryContent';
import InlineLoader from '../inline-loader/inline_loader';
import {ForecastQueryRenderer} from '../../../../ForecastQueryRenderer';
import TaskHierarchySection, {TaskHierarchySectionQuery} from './sections/task-hierachy-section/TaskHierarchySection';
import {hasFeatureFlag} from '../../util/FeatureUtil';
import BreadcrumbsSection from './sections/BreadcrumbsSection';
import DeleteTaskMutation from '../../../../mutations/delete_task_mutation';
import {createToast} from '../toasts/toast';
import {MODAL_TYPE, showModal} from '../modals/generic_modal_conductor';
import {cloneDeep} from 'lodash';
import {hasPermission, isClientUser} from '../../util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../Permissions';
import IntegrationSection from './sections/IntegrationSection';
import {PipedriveIcon} from '@/web-components';
import ProjectUtil from '../../util/ProjectUtil';
import CustomFieldsSection, {customFieldsSectionQuery} from './sections/custom_fields_section';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import {trackTaskModalViewed} from '../../../../tracking/tracking_types/TrackTaskModalViewed';
import {canLoggedInPersonManageProgram, hasTopDownProgramBudgetFeature} from '../../util/ProgramFinancialLogic';
import {ACTION} from '../modals/program-management/ProgramBudgetErrorMessage';
import {recentVisitedTaskKey} from '../../../navigation/header/global-search/GlobalSearchModal';
import {isBillableSplitAllowed} from '../../util/cache/TimeRegistrationSettingsUtil';
import CustomScrollToNativeScroll from '../scroll-bars/CustomScrollToNativeScroll';
import {pathIncludesTask, removeTaskLinkFromUrl} from '../../util/UrlUtil';

let actualTaskId;
// This will not always be up to date. But close enough
let hasCover;

// The PlateJS comment composer's own reserved height. Unlike the legacy editor (whose
// margin is measured live off the DOM, see CommentFooterSection's onTextChanged/onInputExpanded),
// this path sets inputExpanded once here and never recalculates it, so any composer chrome this
// path adds has to be accounted for by hand at this same spot.
const PLATE_COMMENT_COMPOSER_HEIGHT = 97;
// The Nova reply/comment mode switch row CommentFooterSection renders above the editor once a
// task has a saved Nova thread: the design system's SegmentedControl height (44px) plus its own
// root padding (2px top + 2px bottom) plus the row's own 8px margin below it, separating it from
// the editor. Added on top of the composer's base reservation only for a task whose Nova thread
// already exists when the modal mounts - the composer only ever renders that row for exactly that
// task (see CommentFooterSection.hasNovaThread), so a task with no thread keeps the original,
// un-bumped reservation.
const NOVA_REPLY_MODE_ROW_HEIGHT = 56;
/**
 * Whether a paste that reached the window listener has already been dealt with by the
 * editor it landed in, and so must not also be attached to the task.
 *
 * A Plate editor that inlines a pasted image takes the event off the window entirely
 * (`stopImmediatePropagation` on the native event), so that case never arrives here at
 * all. What does still arrive is a paste a Plate editor kept as *content*: copying a
 * spreadsheet range, or Chrome's own "Copy image", hands over markup and a bitmap
 * together, `ImageInsertTarget` hands it back to Slate's deserializer rather than
 * claiming it, and the bitmap must not become an attachment on top of the markup Plate
 * inserted.
 *
 * Both halves are read off the event rather than off `defaultPrevented`, which carries
 * no usable signal here:
 *
 * - Draft.js — the classic editor, rendered whenever the plate_js flags are off — calls
 *   `preventDefault()` on the first line of every paste it receives, before it has looked
 *   at the clipboard, and does nothing with files at all. Reading that flag as "an editor
 *   claimed this" is what stopped pasted images attaching in the classic editor.
 * - Slate only calls it on some browsers: `!HAS_BEFORE_INPUT_SUPPORT ||
 *   isPlainTextOnlyPaste(e) || IS_WEBKIT` (slate-react 0.99.0). On Chrome and Firefox a
 *   rich paste goes through `beforeinput` instead and never sets it, so a guard resting on
 *   it protects the Plate case on Safari only.
 *
 * `data-slate-editor` is Slate's own marker for its editable, and what Slate itself
 * matches on to recognise its own DOM; only the Plate editors carry it. The two flags roll
 * out per surface, so the description and the comments can be running different editors at
 * the same time — reading the DOM under the caret gets each of them right without asking
 * which flags are on.
 *
 * The `text/html` test is the same rule `ImageInsertTarget` uses to decide it is looking at
 * a rich-text paste rather than an image paste, so the two stay in step by construction.
 */
const pasteWasConsumedByEditor = e => {
	const target = e.target;
	if (!(target instanceof Element) || !target.closest('[data-slate-editor]')) return false;
	return Array.from(e.clipboardData?.types ?? []).includes('text/html');
};
const fileTarget = {
	drop(props, monitor) {
		// A drop that landed inside the description or a comment has already been
		// handled there — the editor inlines the image rather than attaching it, and
		// deliberately does not set the task's cover from it.
		if (monitor.didDrop()) return;

		const filesToUpload = monitor.getItem().files.map(file => {
			return {
				taskId: actualTaskId,
				mimeType: file.type,
				name: file.name,
				file,
			};
		});
		let coverFile = null;
		if (!hasCover) {
			coverFile = filesToUpload.find(f => {
				const ext = f.name.split('.').pop();
				return ext === 'png' || ext === 'jpg' || ext === 'gif';
			});
		}

		const onSuccess = result => {
			if (!hasCover && coverFile && result) {
				hasCover = true;
				Util.CommitMutation(UpdateTaskMutation, {
					ids: [actualTaskId],
					coverFileId: result.createFile.file.node.id,
				});
			}
		};
		Util.uploadFiles(filesToUpload, onSuccess, coverFile, MAX_FILES_PER_UPLOAD);
	},
};

class taskModalV3 extends Component {
	constructor(props) {
		super(props);

		const isClientViewRestricted = Util.isClientTaskViewRestricted(this.props.viewer);
		const isClientActionsRestricted = Util.isClientTaskActionsRestricted(this.props.viewer);

		this.state = {
			footerBottomPosition: 0,
			notFound: !this.props.viewer.task || !this.props.viewer.task.project || !this.props.viewer.task.fullAccessToProject,
			showLoader: true,
			height: 456,
			isDependencySectionExpanded: localStorage.getItem('task-modal-v3-dependencies-section-expanded') || false,
			dependenciesSectionAddedByUser: false,
			subtaskSectionAddedByUser: false,
			showComments: localStorage.getItem('task-modal-v3-comment-section-expanded') || false,
			showTimer:
				localStorage.getItem('task-modal-show-timer') && localStorage.getItem('task-modal-show-timer') === 'true',
			forceRerender: false,
			showFooterSection: !isClientViewRestricted && !isClientActionsRestricted,
			isClientViewRestricted,
			isClientActionsRestricted,
			inputExpanded: 0,
			// Set once, by a Nova conversation discarded from inside this modal. The task query runs
			// on mount and is never refetched, so this is the only thing that can tell the thread
			// section and the comment footer - both fed from that one fetched list - that the
			// conversation they are rendering and offering replies on is no longer there.
			novaThreadDiscarded: false,
		};

		this.onResize = this.onResize.bind(this);
		this.onPaste = this.onPaste.bind(this);

		// Inline images removed from the text during this session. Resolved once, on
		// unmount, so that removing an image and then undoing does not cost the user
		// their content — see createPendingMediaDeletes.
		this.pendingMediaDeletes = createPendingMediaDeletes();
		// The editors' own last serialized values. Closing the modal blurs to trigger
		// a save and navigates in the same tick, so the Relay copy is still stale when
		// unmount runs; refcounting against it would delete a just-re-added image.
		this.lastSerialized = {description: undefined, comments: {}};
		this.noteInlineImageRemovals = this.noteInlineImageRemovals.bind(this);

		actualTaskId = props.viewer.task ? props.viewer.task.id : null;
		hasCover = props.viewer.task ? props.viewer.task.coverFile !== null : false;

		// Decided once, right here, rather than re-derived on every render: this is "where did the
		// user land when this modal opened", not a live fact that should keep re-answering itself as
		// props change later - an incoming socket update to the task must not silently re-scroll the
		// thread or reset the composer back into reply mode out from under someone already typing.
		this.novaLandingKind = this.computeNovaLandingKind();
	}

	componentDidMount() {
		this.onResize = this.onResize.bind(this);
		window.addEventListener('resize', this.onResize);
		window.addEventListener('paste', this.onPaste);
		const height = this.taskModalContent ? this.taskModalContent.getBoundingClientRect().height : 0;
		const hasNovaThread = Array.isArray(this.props.viewer.task?.novaThread) && this.props.viewer.task.novaThread.length > 0;
		const plateInputExpanded = hasNovaThread
			? PLATE_COMMENT_COMPOSER_HEIGHT + NOVA_REPLY_MODE_ROW_HEIGHT
			: PLATE_COMMENT_COMPOSER_HEIGHT;
		this.setState({
			height,
			showLoader: false,
			inputExpanded: hasFeatureFlag('plate_js_task_modal_comments') ? plateInputExpanded : 0,
		});

		if (this.props.viewer.task && this.props.viewer.task.project) {
			const projectIds = parseInt(atob(this.props.viewer.task.project.id).replace('ProjectType:', ''));
			const taskId = parseInt(atob(this.props.viewer.task.id).replace('Task:', ''));
			const socketEvents = [
				{
					type: SOCKET_EVENT_TYPE.PROJECT,
					action: SOCKET_ACTION.DELETE,
					projectIds: projectIds,
				},
				{
					type: SOCKET_EVENT_TYPE.PROJECT,
					action: SOCKET_ACTION.UPDATE,
					projectIds: projectIds,
				},
				{
					type: SOCKET_EVENT_TYPE.TASK,
					action: SOCKET_ACTION.UPDATE,
					taskIds: taskId,
				},
				{
					type: SOCKET_EVENT_TYPE.TASK,
					action: SOCKET_ACTION.DELETE,
					taskIds: taskId,
				},
				{
					type: SOCKET_EVENT_TYPE.TIME_REG,
					action: SOCKET_ACTION.UPDATE,
					taskIds: taskId,
				},
				{
					type: SOCKET_EVENT_TYPE.TIME_REG,
					action: SOCKET_ACTION.CREATE,
					taskIds: taskId,
				},
				{
					type: SOCKET_EVENT_TYPE.TIME_REG,
					action: SOCKET_ACTION.DELETE,
					taskIds: taskId,
				},
				{
					type: SOCKET_EVENT_TYPE.SUB_TASK,
					action: SOCKET_ACTION.UPDATE,
					taskIds: taskId,
				},
				{
					type: SOCKET_EVENT_TYPE.SUB_TASK,
					action: SOCKET_ACTION.CREATE,
					taskIds: taskId,
				},
				{
					type: SOCKET_EVENT_TYPE.SUB_TASK,
					action: SOCKET_ACTION.DELETE,
					taskIds: taskId,
				},
				{
					type: SOCKET_EVENT_TYPE.COMMENT,
					action: SOCKET_ACTION.UPDATE,
					taskIds: taskId,
				},
				{
					type: SOCKET_EVENT_TYPE.COMMENT,
					action: SOCKET_ACTION.CREATE,
					taskIds: taskId,
				},
				{
					type: SOCKET_EVENT_TYPE.COMMENT,
					action: SOCKET_ACTION.DELETE,
					taskIds: taskId,
				},
				{
					type: SOCKET_EVENT_TYPE.FILE,
					action: SOCKET_ACTION.UPDATE,
					taskIds: taskId,
				},
				{
					type: SOCKET_EVENT_TYPE.FILE,
					action: SOCKET_ACTION.CREATE,
					taskIds: taskId,
				},
				{
					type: SOCKET_EVENT_TYPE.FILE,
					action: SOCKET_ACTION.DELETE,
					taskIds: taskId,
				},
				{
					type: SOCKET_EVENT_TYPE.SPRINT,
					action: SOCKET_ACTION.DELETE,
					projectIds: projectIds,
				},
				{
					type: SOCKET_EVENT_TYPE.SPRINT,
					action: SOCKET_ACTION.CREATE,
					projectIds: projectIds,
				},
				{
					type: SOCKET_EVENT_TYPE.SPRINT,
					action: SOCKET_ACTION.UPDATE,
					projectIds: projectIds,
				},
				{
					type: SOCKET_EVENT_TYPE.PHASE,
					action: SOCKET_ACTION.DELETE,
					projectIds: projectIds,
				},
				{
					type: SOCKET_EVENT_TYPE.PHASE,
					action: SOCKET_ACTION.CREATE,
					projectIds: projectIds,
				},
				{
					type: SOCKET_EVENT_TYPE.PHASE,
					action: SOCKET_ACTION.UPDATE,
					projectIds: projectIds,
				},
			];
			if (!hasPermission(PERMISSION_TYPE.PROJECTS_READ_ALL)) {
				socketEvents.push({
					type: SOCKET_EVENT_TYPE.PROJECT_PERSON,
					action: SOCKET_ACTION.DELETE,
					projectIds: projectIds,
					personIds: this.props.viewer.backendId,
				});
			}
			this.props.setSocketConfig(socketEvents);
		}

		if (this.props.viewer.task) {
			tracking.trackEvent('Task modal view', {_SubTask: !!this.props.viewer.task.parentTaskId});
			trackEvent('Task Modal', 'Opened');
			trackTaskModalViewed({
				companyId: this.props.viewer.company.id,
				personId: this.props.viewer.actualPersonId,
				taskId: this.props.viewer.task.id,
			});

			localStorage.setItem(recentVisitedTaskKey, this.props.viewer.task.id);
		}
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		if (!this.props.viewer.task || !this.props.viewer.task.project || !this.props.viewer.task.fullAccessToProject) {
			this.setState({notFound: true});
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.onResize);
		window.removeEventListener('paste', this.onPaste);

		const task = this.props.viewer?.task;
		if (task) {
			this.pendingMediaDeletes.resolve(task.id, {
				description: this.lastSerialized.description !== undefined ? this.lastSerialized.description : task.description,
				comments: (task.comments?.edges ?? []).map(edge =>
					this.lastSerialized.comments[edge.node.id] !== undefined
						? this.lastSerialized.comments[edge.node.id]
						: edge.node.comment
				),
			});
		}
	}

	// Where this task modal lands: 'unread' brings the most recent answer into view, 'read' opens
	// the composer straight into replying to it, and null (a task with no Nova thread at all) means
	// this changes nothing about where the modal opens. See novaLanding.ts for the read-state check
	// itself - this only reads the two things needed to ask it.
	computeNovaLandingKind() {
		const task = this.props.viewer.task;
		return deriveNovaLandingKind(task ? task.novaThread : null, task ? task.id : null);
	}

	// Brings the Nova thread's most recent answer into view. NovaAgentPicker.tsx closes itself on
	// any capture-phase document scroll outside its own popover (see scrollToBottom's own comment
	// above), and this is exactly that kind of scroll, so it applies the same guard before handing
	// the node to the same ref-based scrolling scrollToRef already provides for the subtask input.
	revealNovaAnswer(node) {
		if (!node) return;
		if (document.querySelector('[data-cy="nova-agent-picker"]')) return;
		this.scrollToRef(node);
	}

	/**
	 * The image adapter shared by all three editors on this task.
	 *
	 * Built here rather than per surface because every one of them is a class
	 * component, so the hook form is unavailable, and because a single instance is
	 * what we want anyway.
	 *
	 * Keyed on the task alone, deliberately. Keying it on the file set as well
	 * looked right — `adoptReference` needs to know which files are already ours —
	 * but it made every successful inline upload change the adapter's identity,
	 * because `create_file_mutation` RANGE_ADDs onto `Task_files`. That means
	 * `RichTextEditor` rebuilds its plugin list, and plate-core re-deserializes the
	 * whole document, at the exact moment the user finished inserting an image.
	 *
	 * And it bought nothing: plate installs `withImageAdoption` through
	 * `withOverrides` only when the editor is constructed, so the newer adapter's
	 * `adoptReference` never replaces the one already installed. The rebuild cost
	 * was real and the staleness it was meant to fix remained.
	 *
	 * So the ids are handed over as a live read instead of a snapshot.
	 */
	getInlineMediaAdapter(task) {
		// Still keyed on the task id alone, deliberately. `intl` is captured when the
		// adapter is built rather than compared here: rebuilding on a new intl shape
		// would rebuild the editor's plugin list and re-deserialise the whole document,
		// and the toasts read it through the closure anyway.
		if (this.inlineMediaAdapterTaskId !== task.id) {
			this.inlineMediaAdapterTaskId = task.id;
			this.inlineMediaAdapter = createInlineMediaAdapter(
				task.id,
				this.getOwnInlineMediaFileIds,
				this.props.intl,
				this.openInlineFile
			);
		}

		return this.inlineMediaAdapter;
	}

	/**
	 * The task's file ids as they are right now, read straight from props so
	 * adoption is correct however long ago the editor was constructed.
	 */
	getOwnInlineMediaFileIds = () => {
		const edges = this.props.viewer?.task?.files?.edges ?? [];
		return new Set(edges.map(edge => edge.node.id));
	};

	/**
	 * Opens the file behind an inline chip.
	 *
	 * The same judgement the Files list already makes, in all three of its parts: the
	 * types the preview modal can show open in it, video on Safari is refused the
	 * preview, and everything else opens the asset endpoint — which serves with
	 * Content-Disposition: attachment and therefore downloads. Deciding by extension
	 * rather than by the name stored on the node, because the file row is authoritative
	 * and a stored name can be stale after a rename.
	 *
	 * A chip whose file is no longer on the task does nothing rather than opening a
	 * URL that would 404 — the file was deleted, and the chip is about to disappear
	 * from the text on the next save anyway.
	 */
	openInlineFile = fileId => {
		const edges = this.props.viewer?.task?.files?.edges ?? [];
		const file = edges.map(edge => edge.node).find(node => node.id === fileId);
		if (!file) {
			return;
		}

		const src = DirectApi.fileSrc(fileId);
		const isVideo = Util.FileIsVideo(file);
		// Safari is refused the video preview, matching what the Files list already does
		// for the same file. The preview modal plays video through a <video> element that
		// Safari cannot drive without byte-range responses on every path it uses, so the
		// preview there is a dead control; opening the file directly is the honest answer.
		const previewable = Util.FileIsImageOrVideo(file) && !(isVideo && Util.IsSafari());

		if (!previewable) {
			window.open(src, '_blank', 'noopener,noreferrer');
			return;
		}

		// `files` and `isFileVideoSet` are BOTH required, and omitting either is a defect
		// rather than a tidiness question.
		//
		// Without `files`, FileModal's window keydown handler dereferences
		// `this.props.files.length` on the first arrow key — only its render path guards
		// for the prop — and the throw escapes from a window listener, so no ErrorBoundary
		// catches it and the modal wedges open. A one-element array also hides the
		// prev/next chrome, which is the right affordance for a chip that opens one file.
		//
		// Without `isFileVideoSet`, the modal never learns this is a video and falls
		// through to rendering `<img src={videoUrl}>` — a broken image with no playback
		// and no download. The playback cap makes that a routine case rather than an edge
		// one: an over-cap video is deliberately a chip, and clicking it lands here.
		showModal({
			type: MODAL_TYPE.EXPAND_FILE,
			file: src,
			files: [src],
			isFileVideoSet: isVideo ? new Set([src]) : new Set(),
		});
	};

	/**
	 * Records inline images that just left a surface, and what that surface now
	 * holds. Called by each editor as it saves, so the pending set and the final
	 * content both come from the editors rather than from the store.
	 */
	noteInlineImageRemovals(surface, previousContent, nextContent) {
		const removed = removedReferences(previousContent, nextContent);
		removed.forEach(fileId => this.pendingMediaDeletes.note(fileId));
		trackInlineMediaRemoved(removed.length);

		if (surface === 'description') {
			this.lastSerialized.description = nextContent;
		} else {
			this.lastSerialized.comments[surface] = nextContent;
		}
	}

	onResize() {
		//Force render so that window.innerHeight that is used for scrollbar div max height is updated
		requestAnimationFrame(() => {
			this.forceUpdate();
		});
	}

	onPaste(e) {
		// Nothing pasted into a task nobody may edit becomes a file. The upload runs before
		// anything else could refuse it, and in simulation mode it would be a real mutation
		// against a task the user is only modelling. The drop path is closed the same way —
		// the drop target is simply not connected when the task is read only — and so is the
		// attach menu, which renders `disabled={isTaskReadOnly}`.
		if (this.props.viewer.task.readOnly?.isReadOnly || isSimulationMode()) return;

		// This listener is on `window`, so it sees pastes from anywhere in the modal.
		if (pasteWasConsumedByEditor(e)) return;

		if (!e.clipboardData.items || !e.clipboardData.items.length) return;
		const filesToUpload = [];
		for (let i = 0; i < e.clipboardData.items.length; i++) {
			const file = e.clipboardData.items[i].getAsFile();
			if (!file) continue;
			filesToUpload.push({
				taskId: this.props.viewer.task.id,
				mimeType: file.type,
				name: file.name,
				file,
			});
		}
		if (!filesToUpload.length) return;

		let coverFile = null;
		if (!this.props.viewer.task.coverFile) {
			coverFile = filesToUpload.find(f => {
				const ext = f.name.split('.').pop();
				return ext === 'png' || ext === 'jpg' || ext === 'gif';
			});
		}

		const onSuccess = result => {
			if (!this.props.viewer.task.coverFile && coverFile && result) {
				hasCover = true;
				Util.CommitMutation(UpdateTaskMutation, {
					ids: [this.props.viewer.task.id],
					coverFileId: result.createFile.file.node.id,
				});
			}
		};
		Util.uploadFiles(filesToUpload, onSuccess, coverFile, MAX_FILES_PER_UPLOAD);
	}

	/** Opens another task from inside this modal — the breadcrumb's parent, a subtask row, a
	 * dependency. Which mechanism is right depends on how THIS modal was opened, so the decision
	 * lives here rather than in each section.
	 *
	 * A modal opened in place is not in the URL, and the page underneath may be using the path for
	 * its own routing, so the usual "append `/T{id}` to whatever is on screen" would navigate that
	 * page somewhere it cannot come back from. Swap the task in place instead. */
	openTask(companyTaskId) {
		if (this.props.openedInPlace) {
			showModal({type: MODAL_TYPE.TASK_MODAL, taskId: String(companyTaskId), openedInPlace: true}, true);
		} else {
			Util.showTaskModal(companyTaskId, this.props.history);
		}
	}

	closeModal() {
		//Trigger blur so that if any of the inputs is focused, we send the mutation
		if (document.activeElement) {
			document.activeElement.blur();
		}
		// The blur above reaches every field that commits on losing focus, but not the Plate
		// description: that one commits on a click outside itself, and this modal dismisses
		// on `mousedown` — so it unmounts in the gap before the click exists and the edit is
		// discarded (PIKA-745). Ask it for its content while it is still mounted.
		//
		// Before the unmount, deliberately: the export serialises by rendering the editor,
		// which React will not do from inside the commit that tears the tree down, and
		// `componentWillUnmount` resolves the inline-image deletions against whatever the
		// description last reported. Both need this to have already run.
		this.descriptionSection?.flushDescription();
		// A modal opened in place never put itself in the URL, so there is nothing to navigate back
		// from: dismiss it off the conductor's stack. This is taken from how the modal was opened
		// rather than by reading `window.location`, which would guess wrong the moment anything else
		// on the page puts a task in the path.
		if (this.props.openedInPlace) {
			this.props.closeModal();
			return;
		}
		const path = window.location.pathname;
		if (pathIncludesTask(path)) {
			// Task modal is open. Redirect.
			let newUrl = removeTaskLinkFromUrl(path);
			newUrl = Util.appendURLTag(newUrl);
			this.props.history.push(newUrl, {taskModalClosed: true});
		} else {
			// Opened without the URL by something that did not say so. Popping the stack is the only
			// way to dismiss it; the close button, backdrop and Escape are inert otherwise.
			this.props.closeModal();
		}
	}

	scrollToBottom() {
		// NovaAgentPicker.tsx closes itself on any capture-phase document scroll that didn't originate
		// inside its own popover (see its scroll-close effect), so animating a scroll here while the
		// Nova thread's "Change agent" picker is open would slam it shut mid-interaction - most
		// plausibly when a comment is submitted, the picker is opened while that mutation is still in
		// flight, and this fires from the mutation's success callback right after.
		// The picker's own open/closed state is internal to that component and can go false via an
		// outside click, Escape, or its own scroll-close without emitting anything this component could
		// listen for, so a mirrored "is it open" flag here would drift out of sync with reality - a
		// live DOM check is the only way to read its actual current state at scroll time.
		if (document.querySelector('[data-cy="nova-agent-picker"]')) return;
		if (this.customScrollDiv) this.customScrollDiv.animateScrollTop(this.customScrollDiv.scrollbars.getScrollHeight());
	}

	scrollToRef(ref, position) {
		// customScrollDiv is not wired to anything today, so every scroll below is a no-op — but an
		// unguarded `this.customScrollDiv.scrollbars` throws rather than doing nothing, and a throw
		// here unmounts whatever called it. scrollToBottom already guards for the same reason. The
		// existing callers pass section refs that findDOMNode resolves to null, so they return above
		// before reaching the dereference; a caller that passes a real node does not.
		if (!this.customScrollDiv) return;
		if (findDOMNode(ref)) {
			// check if the subtask input is not visible and scrolls to it. If it is visible nothing happens
			const {bottom, top} = findDOMNode(ref).getBoundingClientRect();
			if (position !== 'top') {
				if (bottom < 260 || bottom > window.innerHeight - 100) {
					const newScrollTopValue =
						bottom +
						this.customScrollDiv.scrollbars.getScrollTop() -
						216 -
						this.customScrollDiv.scrollbars.getClientHeight();
					requestAnimationFrame(() => {
						this.customScrollDiv.scrollbars.scrollTop(newScrollTopValue);
					});
				}
			} else {
				const newScrollTopValue = top + this.customScrollDiv.scrollbars.getScrollTop() - 216;
				requestAnimationFrame(() => {
					this.customScrollDiv.scrollbars.scrollTop(newScrollTopValue);
				});
			}
		}
	}

	toggleActivities() {
		const footerBottomPosition = this.taskModalInnerContent.getBoundingClientRect().height + 47; // 47 from absolute positioned comment input
		this.setState({footerBottomPosition});
	}

	onInputExpanded(val) {
		this.setState({inputExpanded: val});
	}

	addDependenciesSection() {
		if (!this.state.dependenciesSectionAddedByUser) {
			tracking.trackSectionAdded({section: 'dependecies'});
			trackEvent('Dependencies', 'Section Added');
		}
		this.setState({dependenciesSectionAddedByUser: !this.state.dependenciesSectionAddedByUser}, () =>
			this.scrollToRef(this.dependencySection, 'top')
		);
		this.toggleDependencySection(true);
	}

	addSubtaskSection() {
		const isTwoLevelSubTask = this.props.viewer.task.project.taskLevels === 2;
		if (!this.state.subtaskSectionAddedByUser) {
			tracking.trackSectionAdded({section: isTwoLevelSubTask ? 'subtask' : 'todo'});
			trackEvent('Subtask', 'Section Added');
		}
		this.setState({subtaskSectionAddedByUser: !this.state.subtaskSectionAddedByUser}, () =>
			this.scrollToRef(this.subtaskSection, 'top')
		);

		// Because animate.css uses transform to animate, it breaks the drag and drop functionality
		// Therefore we rerender without the animation classes on the container
		if (!this.state.forceRerender) {
			setTimeout(() => {
				this.setState({forceRerender: true});
			}, 900);
		}
	}

	onStatusColumnChange(task, option) {
		const {intl} = this.props;
		Util.changeTaskStatus(task, option, intl, this.props.viewer.company.id);
	}

	deleteTask(task) {
		const affectedStatusColumn = task.statusColumnV2;
		const filters = [affectedStatusColumn.id];
		const project = this.props.viewer.task.project;

		const parentTaskId = task.parentTaskId;
		const deletedFromParent = parentTaskId === this.props.viewer.task.id;
		const parentCompanyTaskId = deletedFromParent ? this.props.viewer.task.companyTaskId : task.parentTask?.companyTaskId;

		const onSuccess = () => {
			createToast({
				duration: 4000,
				message: this.props.intl.formatMessage({
					id: parentCompanyTaskId ? 'task_modal.subtask-has-been-deleted' : 'task_modal.has-been-deleted',
				}),
				actionText: null,
				actionCallback: null,
			});
		};

		if (affectedStatusColumn.projectGroupStatusColumnId) {
			filters.push(affectedStatusColumn.projectGroupStatusColumnId);
		}
		Util.CommitSchedulingModalUpdate(
			DeleteTaskMutation,
			{
				ids: [task.id],
				projectId: project.id,
				filters: filters,
				viewer: this.props.viewer,
				parentTaskId: parentTaskId,
			},
			onSuccess
		);

		if (parentCompanyTaskId) {
			if (deletedFromParent) return;
			this.openTask(parentCompanyTaskId);
		} else {
			this.closeModal();
		}
	}

	stopTimer(task, preventedDelete) {
		showModal({
			type: MODAL_TYPE.TIMER_TIME_REGISTRATION,
			timerActionTaskId: task.id,
			preventedDelete: preventedDelete,
		});
	}

	showDeletePrompt(task) {
		if (this.props.viewer.timerStartDate) {
			if (this.props.viewer.timerTask && task.id === this.props.viewer.timerTask.id) {
				this.stopTimer(task, true);
				return;
			}
		}

		showModal({
			type: MODAL_TYPE.TASK_DELETION_WARNING,
			task,
			deleteCallback: () => this.deleteTask(task),
		});
	}

	toggleDependencySection(shouldExpand) {
		if (shouldExpand === true) {
			this.setState({isDependencySectionExpanded: true}, () =>
				Util.localStorageSetItem('task-modal-v3-dependencies-section-expanded', this.state.isDependencySectionExpanded)
			);
		} else {
			this.setState({isDependencySectionExpanded: !this.state.isDependencySectionExpanded}, () =>
				this.state.isDependencySectionExpanded
					? Util.localStorageSetItem(
							'task-modal-v3-dependencies-section-expanded',
							this.state.isDependencySectionExpanded
					  )
					: localStorage.removeItem('task-modal-v3-dependencies-section-expanded')
			);
		}
	}

	toggleComments(shouldExpand) {
		if (shouldExpand === true) {
			this.setState({showComments: true}, () =>
				Util.localStorageSetItem('task-modal-v3-comment-section-expanded', this.state.showComments)
			);
		} else {
			this.setState({showComments: !this.state.showComments}, () => {
				this.state.showComments
					? Util.localStorageSetItem('task-modal-v3-comment-section-expanded', this.state.showComments)
					: localStorage.removeItem('task-modal-v3-comment-section-expanded');
			});
		}
	}

	showProgramRevenueLockedMessage = () => {
		const program = this.props.viewer.task.project.program;
		const loggedInPersonId = this.props.viewer.actualPersonId;
		const canManageProgram = canLoggedInPersonManageProgram(program?.members, loggedInPersonId);

		showModal({
			type: MODAL_TYPE.PROGRAM_BUDGET_ERROR_MESSAGE,
			action: ACTION.INITIATE_TIMER,
			programName: program?.name,
			canManageProgram,
			programPrefix: program?.prefix,
		});

		trackEvent('Program Budget Error Message Modal', 'Shown');
	};

	toggleTimer() {
		if (
			hasTopDownProgramBudgetFeature() &&
			!isBillableSplitAllowed() &&
			this.props.viewer.task.project.isProgramRevenueLocked &&
			this.props.viewer.task.project.programBudgetType === PROGRAM_BUDGET_TYPE.CAPPED &&
			this.props.viewer.task.billable &&
			!this.state.showTimer
		) {
			this.showProgramRevenueLockedMessage();
			return;
		}

		if (!this.state.showTimer) {
			tracking.trackSectionAdded({section: 'timer'});
			trackEvent('Timer', 'Section Added');
		}
		Util.localStorageSetItem('task-modal-show-timer', !this.state.showTimer);
		this.setState({showTimer: !this.state.showTimer});
	}

	/**
	 * Determine if the task hierarchy sec should be shown
	 * @param hasTaskHierarchyFeatureFlag
	 * @param isJiraProject
	 * @param currentTaskDepth 0: parent, 1: subtask, 2: subsubtask
	 * @returns {boolean}
	 */
	showTaskHierarchySection(hasTaskHierarchyFeatureFlag, currentTaskDepth) {
		if (hasTaskHierarchyFeatureFlag) {
			return currentTaskDepth < 2;
		} else {
			return false;
		}
	}

	render() {
		const {formatMessage} = this.props.intl;
		const {viewer} = this.props;
		const {showFooterSection, isClientActionsRestricted} = this.state;

		if (this.state.notFound) {
			return (
				<NotFoundModal
					title={formatMessage({id: 'card_not_found_modal.title'})}
					message={formatMessage({id: 'card_not_found_modal.info'})}
					closeModal={this.closeModal.bind(this)}
				/>
			);
		}
		const {task} = viewer;
		// The conversation as the modal should present it: the fetched list, unless a discard in this
		// session retired it, in which case both the thread section and the footer's reply mode read
		// the task as having no conversation at all - which is exactly what a refetch would return.
		const novaThread = this.state.novaThreadDiscarded ? null : task?.novaThread;
		if (!task || !task.id || !task.fullAccessToProject) {
			return (
				<NotFoundModal
					title={formatMessage({id: 'card_not_found_modal.title'})}
					message={formatMessage({id: 'card_not_found_modal.info'})}
					closeModal={this.closeModal.bind(this)}
				/>
			);
		}

		const {project} = task;
		const isTaskReadOnly = task.readOnly?.isReadOnly || isSimulationMode();
		const isViewerClientUser = isClientUser();
		const {connectDropTarget, isOver} = this.props;
		const taskHierachyFlag = viewer.task.project.useTaskHierarchy;
		const parentsList = [];
		if (taskHierachyFlag) {
			if (task.parentTask) {
				parentsList.push(task.parentTask);
				if (task.parentTask.parentTask) {
					parentsList.unshift(task.parentTask.parentTask);
				}
			}
		}

		const statusOptions = cloneDeep(project.statusColumnsV2?.edges || [])
			.filter(statusColumn => {
				return (!task.jiraKey || !!statusColumn.node.jiraStatusId) && (!task.vstsId || !!statusColumn.node.adoState);
			})
			.sort((a, b) => {
				const comparisonResult = a.node.order - b.node.order;
				if (comparisonResult > 0) {
					return 1;
				} else if (comparisonResult < 0) {
					return -1;
				} else {
					return 0;
				}
			})
			.map(statusColumn => {
				return {
					label: statusColumn.node.name,
					value: statusColumn.node.id,
					category: statusColumn.node.category,
					projectGroupStatusColumnId: statusColumn.node.projectGroupStatusColumnId,
					clearOption: true,
					clickable: true,
					nestedOptions: [],
					logo: statusColumn.node.jiraStatusId ? 'jira-logo' : statusColumn.node.adoState ? 'ado-logo' : undefined,
				};
			});

		let availableRoles = viewer.company.roles?.edges || [];
		if (project.rateCard && project.rateCard.disabledRoles) {
			const disabledRoles = project.rateCard.disabledRoles.map(role => role.id);
			availableRoles = availableRoles.filter(edge => !disabledRoles.includes(edge.node.id));
		}

		const taskModalContent = (
			<div
				className={
					'task-task-modal-v3-content' +
					(isTaskReadOnly ? ' project-locked' : '') +
					(showFooterSection ? '' : ' footer-section-hidden') +
					// For some reason, the footer is absolutely positioned despite following the DOM-hierarchy. This messes with native scrolling because the footer can grow and shrink on focus. This CSS-class instead converts the footer to statically positioned elements.
					// When the feature flag is rolled out fully, the CSS of the footer elements should just be changed entirely.
					' no-absolute-footer'
				}
				onMouseDown={e => e.stopPropagation()}
				ref={e => (this.taskModalContent = e)}
				data-cy="task-modal-content"
				data-userpilot={'task-modal'}
			>
				{isOver ? (
					<div className="drop-file-overlay">
						<div className="dropdown-icon">
							<div className="dropdown-arrow">
								<svg xmlns="http://www.w3.org/2000/svg" width="23" height="26" viewBox="0 0 23 26">
									<path
										fill="#6E0FEA"
										fillRule="evenodd"
										d="M11.5 0L0 11.062l2.85 2.74L9.484 7.42V26h4.03V7.42l6.636 6.382L23 11.062z"
									/>
								</svg>
							</div>
							<svg xmlns="http://www.w3.org/2000/svg" width="32" height="8" viewBox="0 0 32 8">
								<path
									fill="#6E0FEA"
									fillRule="evenodd"
									d="M28 0v4H4V0H0v4c0 2.206 1.794 4 4 4h24c2.206 0 4-1.794 4-4V0h-4z"
								/>
							</svg>
						</div>
						<div className="dropdown-text">{formatMessage({id: 'task_modal.drop_file'})}</div>
					</div>
				) : null}

				{taskHierachyFlag ? (
					<BreadcrumbsSection
						parentsList={parentsList}
						taskName={task.name}
						history={this.props.history}
						openTask={this.openTask.bind(this)}
						closeModal={this.closeModal.bind(this)}
					/>
				) : (
					<div className="cut-off-corner-box-container">
						<svg width="920" height="15">
							<polygon points="0,0 899,0 920,13, 920,15 0,15" style={{fill: 'white'}} />
						</svg>
					</div>
				)}
				<TopSection
					viewer={viewer}
					task={task}
					project={project}
					isTaskReadOnly={isTaskReadOnly}
					closeModal={this.closeModal.bind(this)}
					addDependenciesSection={this.addDependenciesSection.bind(this)}
					dependenciesSectionAddedByUser={this.state.dependenciesSectionAddedByUser}
					addSubtaskSection={this.addSubtaskSection.bind(this)}
					showDeletePrompt={this.showDeletePrompt.bind(this)}
					statusOptions={statusOptions}
					onStatusColumnChange={this.onStatusColumnChange.bind(this)}
					subtaskSectionAddedByUser={this.state.subtaskSectionAddedByUser}
					toggleTimer={this.toggleTimer.bind(this)}
					showTimer={this.state.showTimer}
					taskHierachyFlag={taskHierachyFlag}
					connectedProject={this.props.connectedProject}
					hasParent={parentsList.length > 0}
					parentTaskId={task.parentTask?.companyTaskId}
					hasChildren={task.hasChildren}
				/>
				<MainSection
					showTimeEntries={!this.state.showLoader}
					viewer={viewer}
					availableRoles={availableRoles}
					task={task}
					project={project}
					isClientUser={isViewerClientUser}
					isTaskReadOnly={isTaskReadOnly}
					closeModal={this.closeModal.bind(this)}
					hasChildren={task.hasChildren}
				/>
				<div ref={e => (this.taskModalInnerContent = e)}>
					<CustomScrollToNativeScroll
						maxHeight={window.innerHeight - 320 - (this.state.inputExpanded ? this.state.inputExpanded : 0)}
						hasFocusableContent
						// Old scroller props
						className={'custom-scrollbar-div'}
						hideTracksWhenNotNeeded={true}
						autoHeight={true}
						marginBottom={this.state.inputExpanded}
						autoHeightMax={window.innerHeight - 320 - (this.state.inputExpanded ? this.state.inputExpanded : 0)}
						renderTrackHorizontal={props => <div {...props} className="track track-horizontal" />}
						renderTrackVertical={props => <div {...props} className="track track-vertical" />}
						renderThumbHorizontal={props => <div {...props} className="thumb thumb-horizontal" />}
						renderThumbVertical={props => <div {...props} className="thumb thumb-vertical" />}
					>
						<OptionsSection
							task={task}
							viewer={viewer}
							company={viewer.company}
							project={project}
							showTimer={this.state.showTimer}
							disabled={isTaskReadOnly || isClientActionsRestricted}
						/>
						<DescriptionSection
							ref={section => (this.descriptionSection = section)}
							viewer={viewer}
							task={task}
							project={project}
							isTaskReadOnly={isTaskReadOnly}
							isClientUser={isViewerClientUser}
							toggleDependencySection={this.toggleDependencySection.bind(this)}
							isDependencySectionExpanded={this.state.isDependencySectionExpanded}
							addDependenciesSection={this.addDependenciesSection.bind(this)}
							onInlineImagesChanged={this.noteInlineImageRemovals}
							imageAdapter={this.getInlineMediaAdapter(task)}
						/>
						{task.pipedriveId && (
							<IntegrationSection
								link={`${viewer.company.pipedriveCompanyDomain}/activities/?selected=${task.pipedriveId}&tab=activity`}
								systemName={'Pipedrive'}
								integrationIcon={<PipedriveIcon size={PipedriveIcon.SIZE.TASK_MODAL} />}
							/>
						)}

						<FilesSection task={task} viewer={viewer} isTaskReadOnly={isTaskReadOnly} />

						<LabelSection viewer={viewer} task={task} disabled={isTaskReadOnly || isClientActionsRestricted} />

						{!isViewerClientUser &&
						!project.isJiraProject &&
						(this.state.dependenciesSectionAddedByUser ||
							task.thisTaskDependsOn?.edges?.length !== 0 ||
							task.dependsOnThisTask?.edges?.length !== 0) ? (
							<ForecastQueryRenderer
								key="query-render-dependency-section"
								query={DependencySectionQueryContentQuery}
								customLoader={() => <InlineLoader />}
								variables={{id: viewer.task.companyTaskId.toString()}}
								render={(relayProps, retry) => {
									return (
										<DependencySectionQueryContent
											{...relayProps}
											retry={retry}
											openTask={this.openTask.bind(this)}
											task={task}
											project={project}
											ref={e => (this.dependencySection = e)}
											isTaskReadOnly={isTaskReadOnly}
											isClientUser={isViewerClientUser}
											projectColor={project.projectColor}
											expanded={this.state.isDependencySectionExpanded}
											addedByUser={this.state.dependenciesSectionAddedByUser}
											toggleDependencySection={this.toggleDependencySection.bind(this)}
										/>
									);
								}}
							/>
						) : null}

						{task.subTasks?.edges?.length || this.state.subtaskSectionAddedByUser ? (
							<SubtaskSection
								ref={e => (this.subtaskSection = e)}
								viewer={viewer}
								task={task}
								project={project}
								disabled={isTaskReadOnly || isClientActionsRestricted}
								scrollToSubtaskInput={this.scrollToRef.bind(this, this.subtaskSection)}
								addedByUser={this.state.subtaskSectionAddedByUser}
								forceRerender={this.state.forceRerender}
							/>
						) : null}

						{this.showTaskHierarchySection(taskHierachyFlag, parentsList.length) && (
							<ForecastQueryRenderer
								key="query-render-task-hierarchy-section"
								query={TaskHierarchySectionQuery}
								customLoader={() => <InlineLoader />}
								variables={{id: viewer.task.companyTaskId.toString()}}
								render={(relayProps, retry) => {
									return (
										<TaskHierarchySection
											{...relayProps}
											retry={retry}
											openTask={this.openTask.bind(this)}
											parentId={task.id}
											projectId={project.id}
											companyId={viewer.company.id}
											companyRoles={availableRoles}
											projectPersons={viewer.task.project.projectPersons?.edges}
											disabled={isTaskReadOnly}
											hideTimeRegistrations={ProjectUtil.projectUsesManualProgress(project)}
											showSuggestions={hasFeatureFlag(
												'suggestions_new_task',
												this.props.viewer.availableFeatureFlags
											)}
											statusOptions={statusOptions}
											onStatusColumnChange={this.onStatusColumnChange.bind(this)}
											showDeletePrompt={this.showDeletePrompt.bind(this)}
											isEstimatedInHours={viewer.task.project.estimationUnit === 'HOURS'}
										/>
									);
								}}
							/>
						)}

						{!this.state.showLoader && viewer.company.githubEnabled && !isViewerClientUser ? (
							<GithubSectionRenderer
								viewer={viewer}
								task={task}
								project={project}
								isTaskReadOnly={isTaskReadOnly}
								isClientUser={isViewerClientUser}
							/>
						) : null}
						<NovaConversationThread
							taskId={task.id}
							novaThread={novaThread}
							isTaskReadOnly={isTaskReadOnly}
							isClientActionsRestricted={isClientActionsRestricted}
							revealMostRecentAnswerOnMount={this.novaLandingKind !== null}
							onRevealMostRecentAnswer={this.revealNovaAnswer.bind(this)}
							onConversationDiscarded={() => this.setState({novaThreadDiscarded: true})}
						/>
						<CommentSection
							ref={e => (this.commentSectionRef = e)}
							viewer={viewer}
							task={task}
							project={project}
							isTaskReadOnly={isTaskReadOnly}
							toggleComments={this.toggleComments.bind(this)}
							showComments={this.state.showComments}
							imageAdapter={this.getInlineMediaAdapter(task)}
							onInlineImagesChanged={this.noteInlineImageRemovals}
						/>
						{Util.hasCustomFields() && (
							<ForecastQueryRenderer
								key="query-render-task-custom-fields"
								query={customFieldsSectionQuery}
								showLoader={false}
								variables={{
									taskId: task.id,
								}}
								render={relayProps => <CustomFieldsSection {...relayProps} />}
							/>
						)}
					</CustomScrollToNativeScroll>
				</div>

				<CommentFooterSection
					viewer={viewer}
					project={project}
					isTaskReadOnly={isTaskReadOnly}
					showFooterSection={showFooterSection}
					task={task}
					novaThread={novaThread}
					isClientActionsRestricted={isClientActionsRestricted}
					startInReplyMode={this.novaLandingKind === 'read'}
					scrollToBottom={this.scrollToBottom.bind(this)}
					onInputExpanded={this.onInputExpanded.bind(this)}
					commentSectionRef={this.commentSectionRef}
					toggleComments={this.toggleComments.bind(this)}
					imageAdapter={this.getInlineMediaAdapter(task)}
				/>

				{showFooterSection && (
					<FooterSection
						viewer={viewer}
						task={task}
						project={project}
						isTaskReadOnly={isTaskReadOnly}
						toggleActivities={this.toggleActivities.bind(this)}
						footerBottomPosition={this.state.footerBottomPosition}
						closeModal={this.closeModal.bind(this)}
						isClientUser={isViewerClientUser}
					/>
				)}
			</div>
		);

		const style = {
			transition: 'all 0.4s',
			height: this.state.height - 89 - 14, // subtract padding and cut-off-corner-box height
			opacity: 0,
		};
		if (this.state.showLoader) {
			return (
				<TaskModalLoader>
					<div style={style}>{taskModalContent}</div>
				</TaskModalLoader>
			);
		}
		return (
			<div data-cy="real-task-modal" className="task-modal-v3" onMouseDown={this.closeModal.bind(this)}>
				{isTaskReadOnly ? taskModalContent : connectDropTarget(taskModalContent)}
			</div>
		);
	}
}

const taskModalV3Query = graphql`
	query taskModalV3_Query($id: String, $withCommentReactions: Boolean!) {
		viewer {
			actualPersonId
			component(name: "task_modal_v3")
			...taskModalV3_viewer @arguments(id: $id)
		}
	}
`;

export {taskModalV3Query};

const task = graphql`
	fragment taskModalV3_task on Task {
		id
		userCanDeleteTask
		userCantDeleteTaskReason
		fullAccessToProject
		approved
		name
		readOnly {
			isReadOnly
		}
		coverFile {
			id
		}
		parentTaskId
		novaThread {
			id
			runId
			threadId
			agentId
			versionNumber
			status
			outcome
			answer
			userMessageText
			ranAt
			failureState
			grounding {
				resolved {
					token
					scope
				}
				# The inputs the resolver found nothing for. Selected because the Based-on list names
				# them too: an answer that reads as incomplete is explained by the source that was
				# empty, and without these the list implies every source was read.
				skipped {
					token
				}
			}
		}
		parentTask {
			id
			companyTaskId
			name
			parentTask {
				id
				companyTaskId
				name
			}
		}
		hasChildren
		companyTaskId
		blocked
		bug
		billable
		highPriority
		startYear
		startMonth
		startDay
		description
		jiraId
		jiraKey
		gitlabIid
		vstsId
		pipedriveId
		isUnit4Task
		estimateForecast
		timeLeft
		deadlineDay
		deadlineMonth
		deadlineYear
		startFrom
		deadlineFrom
		gitlabIid
		gitlabServerIid
		hasLockedTime
		hasInvoicedTime
		favoured
		sageIntacctId
		progressDetails {
			progress
		}
		followers {
			id
			firstName
			lastName
			profilePictureId
			profilePictureDefaultId
		}
		taskLabels {
			label {
				id
				name
				color
			}
		}
		subTasks(first: 1000) @connection(key: "Task_subTasks") {
			edges {
				node {
					id
					estimate
					done
					sortOrder
					name
					description
					startDay
					startMonth
					startYear
					endDay
					endMonth
					endYear
					startFrom
					endFrom
					role {
						id
						name
					}
					person {
						id
						firstName
						lastName
						active
						profilePictureId
						profilePictureDefaultId
					}
				}
			}
		}
		thisTaskDependsOn(first: 1000) @connection(key: "Task_thisTaskDependsOn") {
			edges {
				node {
					id
					type
					completed
					taskDependsOnThis {
						id
						name
						companyTaskId
						startYear
						startMonth
						startDay
						deadlineDay
						deadlineMonth
						deadlineYear
						statusColumnV2 {
							id
							category
						}
					}
					thisDependsOnTask {
						id
						name
						companyTaskId
						startYear
						startMonth
						startDay
						deadlineDay
						deadlineMonth
						deadlineYear
						statusColumnV2 {
							id
							category
						}
					}
				}
			}
		}
		dependsOnThisTask(first: 1000) @connection(key: "Task_dependsOnThisTask") {
			edges {
				node {
					id
					type
					completed
					taskDependsOnThis {
						id
						name
						companyTaskId
						startYear
						startMonth
						startDay
						deadlineDay
						deadlineMonth
						deadlineYear
						statusColumnV2 {
							id
							category
						}
					}
					thisDependsOnTask {
						id
						name
						companyTaskId
						startYear
						startMonth
						startDay
						deadlineDay
						deadlineMonth
						deadlineYear
						statusColumnV2 {
							id
							category
						}
					}
				}
			}
		}
		repeatingTask {
			id
			repeatType
			monday
			tuesday
			wednesday
			thursday
			friday
			saturday
			sunday
			monthlyDay
		}
		statusColumnV2 {
			id
			name
			category
			projectGroupStatusColumnId
		}
		phase {
			id
			name
			startYear
			startMonth
			startDay
			deadlineDay
			deadlineMonth
			deadlineYear
		}
		sprint {
			id
			name
			startYear
			startMonth
			startDay
			endDay
			endMonth
			endYear
		}
		assignedPersons {
			id
			firstName
			lastName
			role {
				id
				name
			}
			profilePictureId
			profilePictureDefaultId
			email
		}
		timeRegistrations(first: 5000) @connection(key: "Task_timeRegistrations") {
			edges {
				node {
					id
					invoiced
					xeroInvoiceId
					minutesRegistered
					lockedInPeriod
				}
			}
		}
		role {
			id
			name
		}
		coverFile {
			id
		}
		googleDriveFiles(first: 10000) @connection(key: "Task_googleDriveFiles") {
			edges {
				node {
					id
					name
					link
					thumb
				}
			}
		}
		files(first: 10000) @connection(key: "Task_files") {
			edges {
				node {
					id
					name
					mimeType
					key
					size
					yearCreated
					monthCreated
					dayCreated
					hourCreated
					minuteCreated
					secondCreated
					person {
						id
						fullName
					}
				}
			}
		}
		# Files linked from the project rather than uploaded here. Same node fields as the files
		# connection above: the row renderer reads them, and a missing one renders blank. The key
		# matches the one the attach mutation appends to and the detach mutation removes from.
		referencedFiles(first: 10000) @connection(key: "Task_referencedFiles", filters: []) {
			edges {
				node {
					id
					name
					mimeType
					key
					size
					yearCreated
					monthCreated
					dayCreated
					hourCreated
					minuteCreated
					secondCreated
					person {
						id
						fullName
					}
				}
			}
		}
		commentCount
		comments(first: 100000) @connection(key: "Task_comments") {
			edges {
				node {
					id
					comment
					year
					month
					day
					hours
					minutes
					seconds
					person {
						id
						firstName
						lastName
						fullName
						profilePictureId
						profilePictureDefaultId
					}
					reactions @include(if: $withCommentReactions) {
						emoji
						count
						viewerHasReacted
						persons {
							id
							fullName
						}
					}
				}
			}
		}
		createdAt
		createdBy {
			id
			firstName
			lastName
		}
		latestUiUpdateAt
		latestUiUpdateBy {
			id
			firstName
			lastName
		}
		owner {
			id
			firstName
			lastName
			fullName
			profilePictureId
			profilePictureDefaultId
			role {
				id
				name
			}
		}
		project {
			id
			name
			useTaskOwner
			useTaskFollowers
			useTaskHierarchy
			projectStartYear
			projectStartMonth
			projectStartDay
			projectEndYear
			projectEndMonth
			projectEndDay
			status
			projectColor
			isJiraProject
			jiraSubtaskType
			jiraCloudProject {
				id
			}
			jiraCloudEpicIds
			jiraServerProject {
				id
			}
			gitlabProjectName
			gitlabServerProjectName
			projectGroupId
			billable
			taskLevels
			companyProjectId
			customProjectId
			...DeprecatedProjectIndicatorJS_project
			billable
			isProgramRevenueLocked
			programBudgetType
			program {
				name
				prefix
				budgetType
				members {
					edges {
						node {
							role
							person {
								id
							}
						}
					}
				}
			}
			harvestProject {
				id
			}
			manualProgressOnProjectEnabled
			manualProgressOnPhasesEnabled
			manualProgressOnTasksEnabled
			vstsProject
			vstsAccount
			vstsTwoWaySync
			sprintTimeBox
			estimationUnit
			remainingAutoCalculated
			rateCard {
				id
				disabledRoles {
					id
				}
			}
			projectPersons(first: 10000) @connection(key: "Project_projectPersons", filters: []) {
				edges {
					node {
						id
						person {
							id
							firstName
							lastName
							fullName
							profilePictureId
							profilePictureDefaultId
							active
							permissions
							harvestUser
						}
						project {
							id
						}
						role {
							id
							name
						}
					}
				}
			}
			client {
				id
				name
				logoId
				logoDefaultId
			}
			phases(first: 10000) {
				edges {
					node {
						id
						name
						startYear
						startMonth
						startDay
						deadlineYear
						deadlineMonth
						deadlineDay
					}
				}
			}
			statusColumnsV2(first: 1000000) {
				edges {
					node {
						id
						name
						category
						order
						jiraStatusId
						adoState
						projectGroupStatusColumnId
						userActions {
							canDelete
							canDeleteWithTasks
							canCreateTask
							canRename
						}
					}
				}
			}
			sprints(first: 10000) {
				edges {
					node {
						id
						name
						startYear
						startMonth
						startDay
						endYear
						endMonth
						endDay
					}
				}
			}
			program {
				name
				prefix
				members {
					edges {
						node {
							role
							person {
								id
							}
						}
					}
				}
			}
		}
	}
`;

export {task};

/** The unwrapped class. The default export is this behind a drag-drop target, intl, the router and
 * a Relay fragment container, none of which a test of the component's own decisions needs. */
export {taskModalV3};

export default DropTarget(NativeTypes.FILE, fileTarget, (connect, monitor) => ({
	connectDropTarget: connect.dropTarget(),
	isOver: monitor.isOver(),
	canDrop: monitor.canDrop(),
}))(
	withSocketHandling(
		injectIntl(
			withRouter(
				createFragmentContainer(taskModalV3, {
					viewer: graphql`
						fragment taskModalV3_viewer on Viewer @argumentDefinitions(id: {type: "String"}) {
							id
							backendId
							actualPersonId
							email
							firstName
							lastName
							harvestUser
							timerStartDate
							timerEndDate
							useDetailedSubtasks
							githubConnected
							profilePictureId
							profilePictureDefaultId
							availableFeatureFlags {
								key
							}
							company {
								...optionsSection_company
								id
								modules {
									moduleType
								}
								pipedriveCompanyDomain
								githubEnabled
								gdriveEnabled
								gitlabServerUrl
								characterLimit
								roles(first: 10000) {
									edges {
										...RoleDropdown_roles
										node {
											id
											name
										}
									}
								}
								labels(first: 10000) {
									edges {
										node {
											id
											name
											category {
												allowOnTasks
												allowOnProjects
												allowOnPeople
											}
											...LabelDropdownJS_labels
										}
									}
								}
							}
							timerTask {
								id
							}
							client {
								id
								name
							}

							task(companyTaskId: $id) {
								...taskModalV3_task @relay(mask: false)
							}
						}
					`,
				})
			)
		)
	)
);
